import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './customer-list.style.css';

function InlineContactPersonForm({ contactPersons, onAdd, onRemove }) {
    const { t } = useTranslation();
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        position: '',
        is_primary: false
    });

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAdd = () => {
        if (!formData.name.trim()) {
            return;
        }

        onAdd(formData);
        setFormData({
            name: '',
            email: '',
            phone: '',
            position: '',
            is_primary: false
        });
        setShowForm(false);
    };

    const handleCancel = () => {
        setFormData({
            name: '',
            email: '',
            phone: '',
            position: '',
            is_primary: false
        });
        setShowForm(false);
    };

    return (
        <div className="form-section">
            <div className="form-section-header">
                <h3 className="form-section-title text-base">
                    {t('customers.contactPersons')}
                </h3>
                <span className="form-hint">{t('common.optional')}</span>
            </div>

            {!showForm && (
                <button
                    type="button"
                    className="btn btn-add mt-2"
                    onClick={() => setShowForm(true)}
                >
                    {t('customers.addContactPerson')}
                </button>
            )}

            {showForm && (
                <div className="form-grid form-grid-2 mt-3 p-4 rounded" style={{background: 'var(--background-elevated)'}}>
                    <div className="form-group">
                        <label htmlFor="contact-name" className="form-label required">
                            {t('customers.contactName') || 'Naam'}
                        </label>
                        <input
                            id="contact-name"
                            type="text"
                            name="name"
                            className="form-input"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder={t('customers.contactNamePlaceholder')}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="contact-position" className="form-label">
                            {t('customers.contactPosition') || 'Functie'}
                        </label>
                        <input
                            id="contact-position"
                            type="text"
                            name="position"
                            className="form-input"
                            value={formData.position}
                            onChange={handleInputChange}
                            placeholder={t('customers.contactPositionPlaceholder')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="contact-email" className="form-label">
                            {t('common.email') || 'Email'}
                        </label>
                        <input
                            id="contact-email"
                            type="email"
                            name="email"
                            className="form-input"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={t('customers.contactEmailPlaceholder')}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="contact-phone" className="form-label">
                            {t('common.phone') || 'Telefoon'}
                        </label>
                        <input
                            id="contact-phone"
                            type="tel"
                            name="phone"
                            className="form-input"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder={t('customers.contactPhonePlaceholder')}
                        />
                    </div>

                    <div className="form-group form-item-full">
                        <label className="form-checkbox">
                            <input
                                type="checkbox"
                                name="is_primary"
                                checked={formData.is_primary}
                                onChange={handleInputChange}
                            />
                            <span className="ml-2">
                                {t('customers.setPrimaryContact')}
                            </span>
                        </label>
                    </div>

                    <div className="form-group form-item-full">
                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={handleCancel}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                className="btn btn-submit"
                                onClick={handleAdd}
                                disabled={!formData.name.trim()}
                            >
                                {t('common.add')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {contactPersons && contactPersons.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-semibold mb-2 text-muted">
                        {t('customers.addedContactPersons') || 'Toegevoegde contactpersonen'}:
                    </h4>
                    <div className="space-y-2">
                        {contactPersons.map((cp, index) => (
                            <div
                                key={index}
                                className="flex items-start justify-between p-3 rounded"
                                style={{background: 'var(--background-elevated)'}}
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold">{cp.name}</span>
                                        {cp.is_primary && (
                                            <span className="status-badge badge-active text-xs">
                                                Primary
                                            </span>
                                        )}
                                    </div>
                                    {cp.position && (
                                        <div className="text-sm text-muted mb-1">
                                            {cp.position}
                                        </div>
                                    )}
                                    {cp.email && (
                                        <div className="text-sm text-muted">
                                            {cp.email}
                                        </div>
                                    )}
                                    {cp.phone && (
                                        <div className="text-sm text-muted">
                                            {cp.phone}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-delete"
                                    onClick={() => onRemove(index)}
                                    aria-label={`${t('common.delete')} ${cp.name}`}
                                    style={{minWidth: 'auto', padding: '0.5rem 1rem'}}
                                >
                                    {t('common.delete')}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

InlineContactPersonForm.propTypes = {
    contactPersons: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

export default InlineContactPersonForm;

